import { useMemo } from "react";
import { Building2, ClipboardCheck, LineChart, Shield, User, GraduationCap } from "lucide-react";
import {
  buildCategoryBars,
  buildMgmtHbarChart,
  buildMgmtSnapshotCards,
  buildMgmtTableRows,
  buildPolicyActions,
  buildPolicyHero,
  buildQualityScorecard,
  fmt,
} from "@/utils/ageEnrolmentDerived";
import "./AgeEnrolmentPolicyTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const SNAPSHOT_ICONS = [Building2, User, Shield, GraduationCap];
const ACTION_ICONS = {
  "": LineChart,
  green: Shield,
  orange: ClipboardCheck,
};

const splitMgmtLabel = (name) => {
  if (!name || name.length <= 16) return [name];
  const paren = name.indexOf("(");
  if (paren > 0) return [name.slice(0, paren).trim(), name.slice(paren).trim()];
  const mid = Math.ceil(name.length / 2);
  const space = name.lastIndexOf(" ", mid);
  if (space > 0) return [name.slice(0, space), name.slice(space + 1)];
  return [name];
};

const AgeEnrolmentPolicyTab = ({
  overview = {},
  managementData = [],
  categoryData = [],
  dataQuality = null,
}) => {
  const hero = useMemo(
    () => buildPolicyHero(managementData, overview, dataQuality),
    [managementData, overview, dataQuality]
  );
  const snapshotCards = useMemo(
    () => buildMgmtSnapshotCards(managementData, overview),
    [managementData, overview]
  );
  const mgmtBars = useMemo(() => buildMgmtHbarChart(managementData), [managementData]);
  const categoryBars = useMemo(() => buildCategoryBars(categoryData), [categoryData]);
  const qualityCards = useMemo(() => buildQualityScorecard(dataQuality), [dataQuality]);
  const tableRows = useMemo(() => buildMgmtTableRows(managementData), [managementData]);
  const actions = useMemo(() => buildPolicyActions(managementData), [managementData]);

  return (
    <div className="aepm-tab" data-testid="age-enrolment-policy">
      <section className="aepm-hero-card" id="aepm-hero">
        <div className="aepm-policy-hero-layout">
          <div>
            <div className="aepm-policy-main">
              <div
                className="aepm-donut-gauge"
                style={hero.donut.style}
                aria-label={`${hero.donut.topName} share ${hero.donut.topSharePct} percent`}
              >
                <div>
                  <strong>{hero.donut.topSharePct}%</strong>
                  <span>Top share</span>
                </div>
              </div>
              <div className="aepm-hero-copy">
                <h2>{toTitleCase(hero.headline)}</h2>
                <p>{hero.body}</p>
                <div className="aepm-status-row">
                  {hero.chips.map((chip) => (
                    <span key={chip.label} className={`aepm-status-chip ${chip.className}`.trim()}>
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="aepm-policy-kpis" aria-label="Policy KPIs">
            {hero.kpis.map((kpi) => (
              <article key={kpi.label} className={`aepm-metric-card ${kpi.cardClass}`.trim()}>
                <small>{kpi.label}</small>
                <strong>{kpi.value}</strong>
                <span>{kpi.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="aepm-section-title">
        <div>
          <h2>Management Snapshot</h2>
          <p>
            Four high-impact management groups are shown first so policy users can instantly
            understand scale, parity and average school size.
          </p>
        </div>
        <button type="button" className="aepm-section-action" onClick={() => scrollToId("aepm-matrix")}>
          View full matrix →
        </button>
      </div>

      <section className="aepm-management-grid">
        {snapshotCards.map((card, idx) => {
          const Icon = SNAPSHOT_ICONS[idx] ?? Building2;
          return (
            <article key={card.key} className="aepm-mgmt-card">
              <div className="aepm-mgmt-head">
                <div className={`aepm-icon-box ${card.iconClass}`.trim()}>
                  <Icon className="aepm-svg-icon" strokeWidth={2} />
                </div>
                <span className={`aepm-status-dot ${card.dotClass}`.trim()} />
              </div>
              <h3>{toTitleCase(card.name)}</h3>
              <div className="aepm-mgmt-value">
                <strong>{card.students}</strong>
                <span>students</span>
              </div>
              <div className="aepm-progress-line">
                <i
                  style={{
                    width: `${card.sharePct}%`,
                    background: card.barColor,
                  }}
                />
              </div>
              <div className="aepm-mgmt-meta">
                <span>{card.schools} schools</span>
                <span>GPI {card.gpi}</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="aepm-section-title">
        <div>
          <h2>Policy Diagnostics</h2>
          <p>
            Charts are separated from the table to reduce visual overload and make the policy
            story easier to explain in review meetings.
          </p>
        </div>
        <button type="button" className="aepm-section-action" onClick={() => scrollToId("aepm-quality")}>
          Check data quality →
        </button>
      </div>

      <section className="aepm-analytics-grid">
        <article className="aepm-panel">
          <div className="aepm-panel-head">
            <div>
              <h3>Enrolment by Management Type</h3>
              <p>Largest management groups ranked by student base and policy coverage.</p>
            </div>
            <span className="aepm-pill">Top groups</span>
          </div>
          <div className="aepm-bar-list" aria-label="Management enrolment bar chart">
            {mgmtBars.map((bar) => {
              const lines = splitMgmtLabel(bar.label);
              return (
                <div key={bar.key} className="aepm-hbar-row">
                  <div className="aepm-hbar-label">
                    {lines.map((line, i) => (
                      <span key={i}>
                        {line}
                        {i < lines.length - 1 ? <br /> : null}
                      </span>
                    ))}
                  </div>
                  <div className="aepm-hbar-track">
                    <div
                      className={`aepm-hbar ${bar.barClass}`.trim()}
                      style={{ width: `${bar.widthPct}%` }}
                    />
                  </div>
                  <div className="aepm-hbar-value">{bar.value}</div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="aepm-panel">
          <div className="aepm-panel-head">
            <div>
              <h3>Enrolment by School Category</h3>
              <p>Category-wise distribution shows where capacity and transition policy should focus.</p>
            </div>
            <span className="aepm-pill amber">Stage mix</span>
          </div>
          <div className="aepm-category-bars" aria-label="School category distribution chart">
            {categoryBars.map((row) => (
              <div key={row.key} className="aepm-category-row">
                <span>{row.name}</span>
                <div className="aepm-category-track">
                  <div className="aepm-category-fill" style={{ width: `${row.widthPct}%` }} />
                </div>
                <b>{row.value}</b>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="aepm-section-title" id="aepm-quality">
        <div>
          <h2>Data Quality and Governance Controls</h2>
          <p>
            Quality metrics are promoted into a review-ready scorecard instead of being hidden
            beside charts.
          </p>
        </div>
        <button type="button" className="aepm-section-action" onClick={() => scrollToId("aepm-actions")}>
          View actions →
        </button>
      </div>

      <section className="aepm-quality-grid">
        {qualityCards.map((card) => (
          <article key={card.label} className="aepm-quality-card">
            <small>{card.label}</small>
            <strong>{card.value}</strong>
            <span>{card.sub}</span>
          </article>
        ))}
      </section>

      <div className="aepm-section-title" id="aepm-matrix">
        <div>
          <h2>Management Performance Matrix</h2>
          <p>
            Table is retained for operational drill-down, but the columns are simplified and
            status tags highlight where review is needed.
          </p>
        </div>
        <button type="button" className="aepm-section-action" onClick={() => scrollToId("aepm-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="aepm-two-column" id="aepm-actions">
        <article className="aepm-panel aepm-table-card">
          <div className="aepm-table-head">
            <div>
              <h3>Enrolment by School Management</h3>
              <p>Key management types sorted by total enrolment.</p>
            </div>
            <span className="aepm-pill">Top policy matrix</span>
          </div>
          <div className="aepm-table-scroll">
            <table aria-label="Enrolment by school management type">
              <thead>
                <tr>
                  <th>Management Type</th>
                  <th className="num">Schools</th>
                  <th className="num">Boys</th>
                  <th className="num">Girls</th>
                  <th className="num">Total</th>
                  <th className="num">GPI</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.name}</td>
                    <td className="num">{row.schools}</td>
                    <td className="num text-blue">{fmt(row.boys)}</td>
                    <td className="num text-pink">{fmt(row.girls)}</td>
                    <td className="num score">{fmt(row.total)}</td>
                    <td className="num">{row.gpi}</td>
                    <td>
                      <span className={`aepm-tag ${row.statusClass}`}>{row.statusLabel}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="aepm-panel">
          <div className="aepm-panel-head">
            <div>
              <h3>Recommended Policy Actions</h3>
              <p>Three focused actions for district review and follow-up.</p>
            </div>
            <span className="aepm-pill amber">Priority</span>
          </div>
          <div className="aepm-action-card">
            {actions.items.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] ?? LineChart;
              return (
                <div key={item.title} className="aepm-action-item">
                  <div className={`aepm-icon-box ${item.iconClass}`.trim()}>
                    <Icon className="aepm-svg-icon" strokeWidth={2} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
            <div className="aepm-action-footer">
              <b>Next review recommendation</b>
              <span>{actions.footer}</span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default AgeEnrolmentPolicyTab;
